import { dashboardHomeSurface } from "./lib/dashboardHomeSurface.js";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { useSearchParams } from "react-router-dom";
import AppNav from "./components/AppNav.jsx";
import UserNotificationBell from "./components/UserNotificationInbox.jsx";
import SidebarFooter from "./components/SidebarFooter.jsx";
import { writeUrlParam } from "./lib/urlParams.js";
import { useUrlParamMerger } from "./lib/useUrlParamMerger.js";
import { resetSharedDetailPanelWidth } from "./components/SplitPage.jsx";
import TabStrip from "./components/TabStrip.jsx";
import {
  initialSidebarCollapsed,
  initialTheme,
  persistShellState,
} from "./lib/shell.js";
import { DEFAULT_RANGE } from "./lib/dateRangePresets.js";
import { usePhoneDrawer } from "./lib/usePhoneDrawer.js";
import { useInvestigationSidebarFold } from "./lib/useInvestigationSidebarFold.js";
import { useWorkerReady } from "./lib/dataClient.js";
import AdminPage from "./pages/AdminPage.jsx";
import SettingsConsole from "./components/settings/SettingsConsole.jsx";
import ManagedAgentConfigPage from "./pages/ManagedAgentConfigPage.jsx";
import { settingForView, settingFromLegacy, settingTitle } from "./lib/settingsModel.js";
import AdminMenu, { ADMIN_VIEWS, MODES, SHOWN_VIEW_MODES, modeForView, firstVisibleViewForMode } from "./components/AdminMenu.jsx";

import {
  ComplianceIntegrationHealthBanner,
  ComplianceIntegrationHealthProvider,
} from "./components/ComplianceIntegrationHealth.jsx";
import CanvasMenu from "./components/CanvasMenu.jsx";
import ChatHomePage from "./pages/ChatHomePage.jsx";
import OverviewPage from "./pages/OverviewPage.jsx";
import WorkspaceHome from "./pages/WorkspaceHome.jsx";
import ExplorePage from "./pages/ExplorePage.jsx";
import { fleetConsoleAvailable, setFleet } from "./lib/fleetConsoleStore.js";
import WorkspaceThreads, { requestThreadsSearch } from "./components/WorkspaceThreads.jsx";
import { showWorkspaceInDeck, startWorkspaceInDeck } from "./lib/deckRequests.js";
import TraceCanvasOverlay from "./components/TraceCanvasOverlay.jsx";
import { closeTraceCanvas, keepTraceCanvas, newTraceCanvas, saveTraceCanvas, useTraceCanvasOpen, useWorkspaceChats } from "./lib/traceCanvasStore.js";
import { useHomeChat } from "./components/CanvasChat.jsx";
import { setWorkspaceSearch } from "./lib/workspaceSearch.js";

import { subscribeDock, getDockChatBar } from "./lib/dockStore.js";

import { experimentalEnabled, securityTabEnabled } from "./lib/featureFlags.js";

import { applyAgentPolicyPilotParams } from "./lib/agentPolicyNavigation.js";
import { subscribeNavigation } from "./lib/navigationStore.js";
import { applyTraceNavParams, workspaceQueryForScope } from "./lib/navIntent.js";

import { ROUTER_PARAM, requestedSetting, requestedViewFor, resolveMode, shownModes } from "./lib/routeModel.js";
import ChatConsole from "./components/ChatConsole.jsx";
import { useHasPermission, useMyPermissions } from "./lib/permissions.js";
import { useTenantEntitlements } from "./lib/entitlements.js";

import { PERMISSIONS } from "./lib/permissionKeys.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./lib/authTokenStore.js";
import { isSigningOut } from "./lib/authBridge.js";
import AuthIdentityGate from "./components/AuthIdentityGate.jsx";
import TenantLinkGate from "./components/TenantLinkGate.jsx";
import StartupScreen from "./components/StartupScreen.jsx";

const ADMIN_VIEW_IDS = new Set(ADMIN_VIEWS.map((v) => v.id));

// Per-step row icon button. Square, no fill, hover bumps to ink so
// the affordance reads as "interactive icon" without competing with
// the row's primary label/value text.
// ── App ───────────────────────────────────────────────────────────

function AppContent({ authContextEpoch }) {
  const [startupError, setStartupError] = useState(null);
  useWorkerReady(setStartupError);
  const [searchParams, setSearchParams] = useSearchParams();

  // ── URL state ──
  const range = searchParams.get("range") || DEFAULT_RANGE;

  // securityTabEnabled() gates the Security rail entry. Its views are already
  // gated out of ADMIN_VIEWS (see AdminMenu), which hides the rail button;
  // dropping the mode here too keeps a stray ?mode=security from landing on
  // an empty Security shell.
  const securityEnabled = securityTabEnabled();
  // Explore is experimental: without the flag, ?mode=explore resolves to the
  // default (Findings) and the effect below rewrites the address to match.
  const VALID_MODES = shownModes({ securityEnabled, experimentalEnabled: experimentalEnabled() });

  const canAnalytics = useHasPermission(PERMISSIONS.ANALYTICS_EXECUTE);
  const canReadSessions = useHasPermission(PERMISSIONS.AI_SESSIONS_READ);
  const permissionPosture = useMyPermissions();
  const entitlements = useTenantEntitlements();

  const canChat = useHasPermission(PERMISSIONS.LLM_CONFIG_READ);
  const canGovern = useHasPermission(PERMISSIONS.AGENT_CONFIG_READ);

  // Everyone lands on the Overview; a reader it cannot show lands on the fleet or Settings.
  const defaultMode = "overview";
  const resolvedMode = resolveMode(searchParams, { validModes: VALID_MODES, defaultMode, viewModes: SHOWN_VIEW_MODES });
  const inventoryMode = firstVisibleViewForMode("endpoints", permissionPosture, entitlements) ? "endpoints" : "settings";
  const currentMode = (!canAnalytics && ["explore", "workspace", "dashboard", "triage", "overview"].includes(resolvedMode)) || ((resolvedMode === "triage" || resolvedMode === "overview") && !canReadSessions)
    ? inventoryMode
    : !canChat && resolvedMode === "dashboard" ? canReadSessions ? "workspace" : inventoryMode : resolvedMode;
  const canvasUp = !!useTraceCanvasOpen();

  // Findings, Fleet and Security have no tab strip: one selected child at a
  // time. Settings keeps its own address (`?setting=`, SettingsConsole).
  const singleViewMode = currentMode === "triage" || currentMode === "endpoints" || currentMode === "security";
  const requestedView = requestedViewFor(searchParams, currentMode, SHOWN_VIEW_MODES);
  const modeView = singleViewMode
    ? (requestedView || firstVisibleViewForMode(currentMode, permissionPosture, entitlements) || (currentMode === "triage" ? "triage" : null))
    : null;

  const adminView = modeView;
  // Fleet is one page, with no list in the sidebar, where the reader can read its console.
  const fleetConsole = currentMode === "endpoints" && adminView === "endpoints" && fleetConsoleAvailable(permissionPosture);

  // The sidebar is open or folded to its icons, the same on every page.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => initialSidebarCollapsed());
  const onDrawerClick = usePhoneDrawer(sidebarCollapsed, setSidebarCollapsed);
  useInvestigationSidebarFold(sidebarCollapsed, setSidebarCollapsed);
  const [theme, setTheme] = useState(() => initialTheme());
  // ── Persistence ──
  useEffect(() => {
    persistShellState({ theme });
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.body.dataset.theme = theme;
  }, [theme]);

  const pageLabel = currentMode === "settings"
    ? settingTitle(requestedSetting(searchParams) ?? settingFromLegacy(searchParams))
    : ADMIN_VIEWS.find((item) => item.id === adminView)?.label || MODES.find((item) => item.id === currentMode)?.label || "Origin";
  useEffect(() => { document.title = `Origin / ${pageLabel}`; }, [pageLabel]);

  // Push-coalescing URL-param writer every navigation routes through: reads fresh
  // from the URL (never react-router's stale `prev`), and coalesces the several
  // pushes one click fires (scope + focus + view) into ONE back/forward stop.
  // See useUrlParamMerger for the full rationale + the microtask coalescing window.
  const mergeUrlParams = useUrlParamMerger(setSearchParams);
  useEffect(() => {
    const raw = searchParams.get("mode");
    // Older Explore review and session links name a mode they no longer open.
    const retiredExplore = raw === "explore" && resolvedMode !== "explore";
    // Agent policy's old Settings links open Governance, without the Settings address.
    const policyLink = resolvedMode === "governance" && raw !== "governance";
    if (!retiredExplore && !policyLink && raw !== "admin" && !(raw === "endpoints" && resolvedMode === "settings")) return;
    mergeUrlParams((next) => {
      next.set("mode", resolvedMode);
      if (policyLink) for (const key of ["setting", "adminView", "settingsView", "view", "governanceTab"]) next.delete(key);
    });
  }, [searchParams, resolvedMode, mergeUrlParams]);

  function resetPanelLayout() {
    setSidebarCollapsed(false);
    resetSharedDetailPanelWidth();
    for (const key of ["dcol", "xcol", "pin"]) writeUrlParam(key, null);
    selectMode(defaultMode);
  }

  function updateRange(value) {
    mergeUrlParams((next) => {
      next.set("range", value);
    });
  }

  // Rail mode picker. Per-mode tab strips mean we don't need to find
  // or open a tab — each mode's tab strip preserves its own state
  // independently. Just flip ?mode= and let each mode render its own
  // strip + selection.
  function selectMode(modeId) {
    if (modeId === "endpoints") setFleet({ view: "agents" });
    if (!VALID_MODES.has(modeId)) return;
    mergeUrlParams((next) => {
      // Always drop legacy single-source params that fight the new
      // mode-aware routing.
      next.delete("menu");
      next.delete("view");
      next.delete("kernel");
      next.delete("canvas");
      for (const key of ["tab", "tabs", "scope", "active", "ui", "focus", "lens", "steers", "pin", "xcol", "hist", "kg", "kgc"]) next.delete(key);
      // A page picked here opens fresh: no Findings case left open from before.
      next.delete(ROUTER_PARAM.TRIAGE_FINDING);
      next.set("mode", modeId);
      // Findings and Fleet have no app-level tab strip: seed a permitted
      // child view. Settings opens on its home.
      if (modeId === "triage" || modeId === "endpoints") {
        const existing = next.get("adminView");
        if (!existing || modeForView(existing) !== modeId) {
          next.set("adminView", firstVisibleViewForMode(modeId, permissionPosture, entitlements) || "");
        }
        next.delete("settingsView");
      }
      if (modeId === "settings") {
        for (const key of ["adminView", "settingsView", "setting"]) next.delete(key);
      }
    }, { push: true });
  }

  // A Settings page asked for by its old id (Chat's settings link, the
  // health banner, the profile menu) opens at its address.
  function openSetting(address) {
    mergeUrlParams((next) => {
      next.set("mode", "settings");
      next.set("setting", address);
      for (const key of ["adminView", "settingsView", "menu", "view"]) next.delete(key);
    }, { push: true });
  }

  function selectAdminView(viewId) {
    const setting = settingForView(viewId);
    if (setting) { openSetting(setting); return; }
    if (!viewId || !ADMIN_VIEW_IDS.has(viewId)) return;
    const mode = modeForView(viewId) || "settings";
    mergeUrlParams((next) => {
      next.set("mode", mode);
      if (viewId === "triage") next.delete("triageFinding");
      if (mode === "triage" || mode === "endpoints" || mode === "settings" || mode === "security") next.set("adminView", viewId);
      next.delete("settingsView");
      next.delete("menu");
      next.delete("view");
    }, { push: true });
  }

  const [navigationError, setNavigationError] = useState(null);
  // A selection of activity: a single machine is Fleet's; anything else opens
  // in Explore, narrowed to it, or (`to` "workspace", as a chat asks) as a
  // workspace holding its search.
  function openScopeExternal(scopePath = [], { to = "explore" } = {}) {
    setNavigationError(null);
    if (!Array.isArray(scopePath)) { setNavigationError("This selection cannot be opened. Use Fleet or search Explore directly."); return; }
    const ids = scopePath.length === 1 ? scopePath[0].ids || [scopePath[0].id].filter(Boolean) : [];
    if (!scopePath.length || (scopePath.length === 1 && scopePath[0].dim === "endpoints" && ids.length === 1)) {
      mergeUrlParams((next) => {
        next.set("mode", "endpoints"); next.set("adminView", "endpoints");
        for (const key of ["settingsView", "view", "menu", "tab", "tabs", "scope", "active", "fleetSearch", "fleetType", "fleetLocation", "fleetEvidence", "fleetGroup"]) next.delete(key);
        if (ids.length) { next.set("fleetView", "endpoints"); next.set("fleetSelected", `endpoint:${ids[0]}`); }
        else { next.delete("fleetView"); next.delete("fleetSelected"); }
      }, { push: true });
      return;
    }
    if (!canAnalytics || !canReadSessions) { setNavigationError("Your current access does not include trace activity."); return; }
    if (to === "explore") {
      try {
        setWorkspaceSearch(workspaceQueryForScope(scopePath));
      } catch { setNavigationError("This selection cannot be opened in Explore. Use Fleet or search Explore directly."); return; }
      mergeUrlParams((next) => {
        next.set("mode", "explore");
        for (const key of ["adminView", "settingsView", "view", "menu", "tab", "tabs", "scope", "active"]) next.delete(key);
      }, { push: true });
      return;
    }
    try {
      const q = workspaceQueryForScope(scopePath);
      const id = newTraceCanvas({ title: "Selected activity", searches: [{ q, label: q }] });
      if (!id) throw new Error("This selection could not be opened in Investigations.");
      saveTraceCanvas(id); showWorkspaceInDeck(id);
      mergeUrlParams((next) => {
        next.set("mode", "workspace");
        for (const key of ["adminView", "settingsView", "view", "menu", "tab", "tabs", "scope", "active"]) next.delete(key);
      }, { push: true });
    } catch { setNavigationError("This selection cannot be opened in Investigations. Use Fleet or search Investigations directly."); }
  }
  function openTraceExternal(intent) {
    if (!intent.traceId) return;
    mergeUrlParams((next) => {
      applyTraceNavParams(next, intent);
      next.set("tab", `trace:${intent.traceId}`);
    }, { push: true });
  }
  // A workspace started or opened outside Workspaces (Explore's search, a
  // session's board over the page) opens there, in the pane worked in. The
  // deck takes a request made before it is on screen once it is; a board
  // open over the page goes into the pane as the home comes up.
  function openInWorkspaces(id = null) {
    if (id) showWorkspaceInDeck(id);
    selectMode("workspace");
  }
  // Restore session bookmarks in the existing workspace, including older links.
  const traceTab = searchParams.get("tab") || "";
  useEffect(() => {
    if (currentMode !== "workspace" || !canAnalytics || !canReadSessions || !traceTab.startsWith("trace:")) return;
    const id = traceTab.slice(6);
    if (!/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)) return;
    const key = keepTraceCanvas({ id });
    if (key) showWorkspaceInDeck(key);
  }, [currentMode, traceTab, canAnalytics, canReadSessions]);
  const applyNavigationRef = useRef(null);
  applyNavigationRef.current = (intent) => {
    if (intent?.kind === "workspace" || intent?.kind === "explore") openScopeExternal(intent.scopePath, { to: intent.kind });
    else if (intent?.kind === "trace") openTraceExternal(intent);
    else if (intent?.kind === "agent-policy-pilot" && canGovern && intent.agentId && intent.releaseId) {
      mergeUrlParams((next) => applyAgentPolicyPilotParams(next, intent), { push: true });
    }
  };
  useEffect(() => subscribeNavigation((intent) => applyNavigationRef.current?.(intent)), []);

  // The homepage hosts chat. Endpoint enrollment is a Settings destination.
  const homeSurface = dashboardHomeSurface({ mode: currentMode, canChat });
  const homeChatActive = homeSurface === "chat";
  const enterHomeChat = () => {
    closeTraceCanvas();
    mergeUrlParams((next) => {
      next.set("mode", "dashboard");
      next.set("chat", "1");
      for (const key of ["adminView", "settingsView", "view", "menu"]) next.delete(key);
    }, { push: true });
  };
  const dockChatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  

  const [homeTabs, setHomeTabs] = useState([]);
  const dashActiveChatId = useMemo(
    () => (dockChatBar?.tabs || []).find((t) => t.active)?.id ?? null,
    [dockChatBar],
  );
  const dashChatTab = (id) => (dockChatBar?.tabs || []).find((t) => t.id === id) || null;
  const isDashTabActive = (_kind, id) => homeChatActive && id === dashActiveChatId;
  const addHomeTab = (kind, id) =>
    setHomeTabs((cur) => (cur.some((t) => t.kind === kind && t.id === id) ? cur : [...cur, { kind, id }]));
  const openDashChat = (id) => { addHomeTab("chat", id); dashChatTab(id)?.onSelect?.(); enterHomeChat(); };
  const newDashChat = () => {
    const empty = (dockChatBar?.tabs || []).find((t) => !t.hasItems);
    if (empty) empty.onSelect?.(); else dockChatBar?.onNew?.();
    enterHomeChat();
  };
  // On the Dashboard, ⌘K opens/focuses the new-chat tab (newDashChat) instead
  // of ChatConsole's default ⌘K — captured so it preempts the global hotkey.
  const newDashChatRef = useRef(null);
  newDashChatRef.current = newDashChat;
  useEffect(() => {
    if (currentMode !== "dashboard" || !canChat) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        e.stopImmediatePropagation();
        newDashChatRef.current?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [currentMode, canChat]);
  const reorderDashTabs = (from, to) =>
    setHomeTabs((cur) => { const next = cur.slice(); const [moved] = next.splice(from, 1); next.splice(to, 0, moved); return next; });
  const closeDashTab = (kind, id) => {
    const idx = homeTabs.findIndex((t) => t.kind === kind && t.id === id);
    if (idx < 0) return;
    const next = homeTabs.filter((_, i) => i !== idx);
    const wasActive = isDashTabActive(kind, id);
    setHomeTabs(next);
    if (kind === "chat") dashChatTab(id)?.onClose?.();
    if (wasActive) {
      const neighbor = next[idx] || next[idx - 1] || null;
      if (neighbor?.kind === "chat") openDashChat(neighbor.id);
      else mergeUrlParams((p) => { p.delete("chat"); }, { push: false });
    }
  };
  // A chat begun in a workspace is that workspace's: Home never shows it, nor
  // keeps a tab for it.
  const workspaceChats = useWorkspaceChats();
  useHomeChat(currentMode === "dashboard" && homeChatActive && !canvasUp);
  useEffect(() => {
    if (currentMode !== "dashboard" || !homeChatActive || dashActiveChatId == null) return;
    if (workspaceChats.has(String(dashActiveChatId))) return;
    addHomeTab("chat", dashActiveChatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, homeChatActive, dashActiveChatId, workspaceChats]);
  useEffect(() => {
    if (!dockChatBar) return;
    const live = new Set((dockChatBar.tabs || []).map((t) => t.id).filter((id) => !workspaceChats.has(String(id))));
    setHomeTabs((cur) => {
      const next = cur.filter((t) => t.kind !== "chat" || live.has(t.id));
      return next.length === cur.length ? cur : next;
    });
  }, [dockChatBar, workspaceChats]);
  const dashStripTabs = useMemo(() => homeTabs.map((t) => {
    // Like every tab strip, the last remaining tab can't be closed.
    const closable = homeTabs.length > 1;
    const ct = dashChatTab(t.id);
    return { id: t.id, label: ct?.label || "New chat", dot: ct?.dot, active: homeChatActive && t.id === dashActiveChatId, onSelect: () => openDashChat(t.id), onClose: closable ? () => closeDashTab("chat", t.id) : undefined };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [homeTabs, homeChatActive, dashActiveChatId, dockChatBar]);

  // ── Sidebar ──

  // CSS moves the width; on a phone the open column lies over the page.
  function handleSidebarToggle() {
    setSidebarCollapsed((v) => !v);
  }

  // The modes this reader may open, in the sidebar's order.
  const navModes = MODES.filter((m) => {
    if (!VALID_MODES.has(m.id)) return false;
    if (m.id === "dashboard" && (!canAnalytics || !canChat)) return false;
    if (m.id === "overview" && (!canAnalytics || !canReadSessions)) return false;
    if (m.id === "triage" && (!canAnalytics || !firstVisibleViewForMode("triage", permissionPosture, entitlements))) return false;
    // Security combines review findings and signal alerts. Its view-level
    // permission gate decides visibility; automatic review being disabled
    // must not hide signal alerts that already exist.
    if (m.id === "security" && !firstVisibleViewForMode("security", permissionPosture, entitlements)) return false;
    if (m.id === "endpoints" && !firstVisibleViewForMode("endpoints", permissionPosture, entitlements)) return false;
    if (m.id === "governance" && !canGovern) return false;
    return true;
  });
  // In the workspace already, its row empties the pane worked in to start
  // another; the workspace that showed stays in the sidebar's list.
  const startsAnother = (id) => id === "workspace" && currentMode === "workspace";

  // ── Render ──

  if (startupError) return <StartupScreen error={startupError} />;

  return (
    <div
      className="page-enter"
      data-shell
      data-nav={sidebarCollapsed ? "folded" : "open"}
    >
      <AppNav
        modes={navModes}
        current={currentMode}
        open={!sidebarCollapsed}
        onToggle={handleSidebarToggle}
        onSelect={(id) => (startsAnother(id) ? startWorkspaceInDeck() : selectMode(id))}
        onHome={resetPanelLayout}
        onPick={onDrawerClick}
        nested={currentMode !== "endpoints" || fleetConsole ? null : <AdminMenu
            activeView={adminView}
            entitlements={entitlements}
            mode={currentMode}
            onSelect={selectAdminView}
          />}
        list={currentMode === "dashboard" ? (
          <CanvasMenu
            chatEnabled={canChat}
            chatBar={dockChatBar}
            chatActive={homeChatActive}
            onOpenChat={openDashChat}
            onNewChat={newDashChat}
          />
        ) : currentMode === "explore" ? (
          // The sessions the search reaches, to read one at a time.
          <WorkspaceThreads range={range} variant="explore" onOpenWorkspace={openInWorkspaces} />
        ) : currentMode === "workspace" ? (
          // The workspace's sessions, kept as a chat harness keeps threads.
          <WorkspaceThreads range={range} />
        ) : null}
        foot={<>
          <UserNotificationBell key={authContextEpoch} onOpenAll={() => openSetting("account/inbox")} />
          <SidebarFooter
            theme={theme}
            onToggleTheme={() => setTheme((v) => (v === "dark" ? "light" : "dark"))}
            onOpenAccount={() => selectAdminView("account")}
            onSendFeedback={() => selectAdminView("report")}
          />
        </>}
      />

      <div data-main data-layout="admin" style={{ gridTemplateRows: "auto minmax(0, 1fr)" }}>
        <div>
          <ComplianceIntegrationHealthBanner onOpenDataSources={() => selectAdminView("data-sources")} />
          {navigationError ? <div role="alert" className="flex items-center justify-between gap-[12px] border-b border-[color:var(--line)] px-[var(--main-inset)] py-[10px] text-[12px]">
            <span>{navigationError}</span><button type="button" onClick={() => setNavigationError(null)}>Dismiss</button>
          </div> : null}
        </div>
        <div className="grid h-full min-h-0 min-w-0 overflow-hidden" style={{ gridTemplateRows: currentMode === "dashboard" ? "auto minmax(0,1fr)" : "minmax(0,1fr)" }}>
                {currentMode === "dashboard" ? (
                  <div className="min-w-0 overflow-hidden px-[var(--main-inset)]">
                    <TabStrip
                      className="pt-[11px] max-[900px]:pt-[8px]"
                      tabs={dashStripTabs}
                      onReorder={reorderDashTabs}
                    />
                  </div>
                ) : null}
                {currentMode === "overview" ? (
                  <OverviewPage range={range} onRangeChange={updateRange} />
                ) : currentMode === "settings" ? (
                  <SettingsConsole
                    entitlements={entitlements}
                    theme={theme}
                    onToggleTheme={() => setTheme((v) => (v === "dark" ? "light" : "dark"))}
                    onScopeNavigate={openScopeExternal}
                  />
                ) : currentMode === "governance" ? (
                  // Agent policy takes the whole page: its editor has three
                  // columns, which Settings' narrow column could not hold.
                  <section data-governance className="h-full min-h-0 min-w-0 overflow-y-auto bg-[color:var(--bg)]">
                    <div className="grid gap-[20px] px-[40px] pb-[64px] pt-[28px] max-[900px]:px-[24px] max-[639px]:px-[16px]">
                      <header>
                        <h1 className="m-0 text-[30px] font-semibold leading-[1.1] tracking-[-0.025em] text-[color:var(--ink)]">Agent policy</h1>
                        <p className="m-0 mt-[6px] text-[14px] leading-[1.45] text-[color:var(--muted)]">What Claude Code, Codex and GitHub Copilot may do on your endpoints</p>
                      </header>
                      {canGovern ? <ManagedAgentConfigPage key={`${searchParams.get("policyAgent") || "all"}:${searchParams.get("policyRelease") || ""}`} initialAgentId={searchParams.get("policyAgent") || "all"} initialReleaseId={searchParams.get("policyRelease") || ""} /> : (
                        <p className="m-0 text-[14px] text-[color:var(--muted)]">Reading agent policy needs agent configuration access. Ask an administrator for it.</p>
                      )}
                    </div>
                  </section>
                ) : adminView ? (
                  <AdminPage
                    entitlements={entitlements}
                    view={adminView}
                    range={range}
                    onScopeNavigate={openScopeExternal}
                  />
                ) : currentMode === "explore" ? (
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]">
                    <ExplorePage range={range} onOpenWorkspace={openInWorkspaces} />
                  </section>
                ) : currentMode === "workspace" ? (
                  // The open workspace: its board and its dock. + Add finds
                  // in the sidebar's sessions, opening the drawer if it is
                  // shut.
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]">
                    <WorkspaceHome
                      range={range}
                      onRangeChange={updateRange}
                      onFind={() => {
                        if (sidebarCollapsed) handleSidebarToggle();
                        setTimeout(requestThreadsSearch, 0);
                      }}
                    />
                  </section>
                ) : currentMode === "dashboard" ? (
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)] px-[var(--main-inset)]">
                    <ChatHomePage chatActive={homeChatActive} />
                  </section>) : null}
        </div>
      </div>

      {/* Keep the console mounted across navigation so a running homepage
          chat can finish. Its UI is hosted by the homepage, and by the dock
          beside an open workspace, where it is that workspace's chat. */}
      {canChat ? <ChatConsole key={authContextEpoch} onOpenSettings={selectAdminView} onOpenPage={enterHomeChat} pageVisible={(homeChatActive && !canvasUp) || (currentMode === "workspace" && canvasUp)} wanted={currentMode === "dashboard" || currentMode === "workspace"} /> : null}
      {/* On the home the open workspace is the page itself. */}
      {currentMode === "workspace" ? null : <TraceCanvasOverlay range={range} onRangeChange={updateRange} onOpenInWorkspaces={canAnalytics ? () => openInWorkspaces() : null} />}
    </div>
  );
}

export default function App() {
  const authIdentityEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
  if (isSigningOut()) return null;
  return (
    <AuthIdentityGate identityEpoch={authIdentityEpoch}>
      <TenantLinkGate>
        <ComplianceIntegrationHealthProvider key={authIdentityEpoch}>
          <AppContent authContextEpoch={authIdentityEpoch} />
        </ComplianceIntegrationHealthProvider>
      </TenantLinkGate>
    </AuthIdentityGate>
  );
}
